import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus } from '../../services/subscriptionService';
import {
  Check,
  X,
  BarChart3,
  Palette,
  Headphones,
  Clock,
  MapPin,
  ArrowRight,
  Crown,
  Package,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FEATURES = [
  { key: 'feature_analytics', label: 'Advanced Analytics', icon: BarChart3, href: '/vendor/advanced-analytics' },
  { key: 'feature_custom_branding', label: 'Custom Branding', icon: Palette, href: '/vendor/branding' },
  { key: 'feature_priority_support', label: 'Priority Support', icon: Headphones, href: '/vendor/support' },
  { key: 'feature_advanced_booking', label: 'Advanced Booking', icon: Clock, href: '/vendor/bookings' },
  { key: 'feature_multi_location', label: 'Multi-Location', icon: MapPin, href: '/vendor/locations' },
];

const VendorPlanFeatures = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (!hasActiveSubscription) {
      navigate('/vendor/subscription', { replace: true });
      return;
    }
    const fetchStatus = async () => {
      try {
        const response = await getVendorStatus();
        if (response.success && response.data.current_subscription) {
          setSubscriptionDetails(response.data.current_subscription);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const features = subscriptionDetails?.features || {};
  const hasFeature = (key) => !!features[key];

  if (isChecking || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-10">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Crown className="w-8 h-8 text-amber-500" />
            Your Plan Features
          </h1>
          <p className="mt-2 text-gray-600">
            {subscriptionDetails?.plan_name && (
              <span className="font-medium text-gray-900">{subscriptionDetails.plan_name}</span>
            )}{' '}
            — Features included in your subscription
          </p>
        </div>

        {/* Feature List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-200">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              const included = hasFeature(feature.key);
              const content = (
                <>
                  <div className="flex items-center gap-3 sm:gap-4">
                    {included ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <Icon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-gray-900">
                      {feature.label}
                    </span>
                  </div>
                  {included ? (
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <Link
                      to="/vendor/subscription"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs sm:text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      Upgrade
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </>
              );
              return included ? (
                <Link
                  key={feature.key}
                  to={feature.href}
                  className="flex items-center justify-between p-4 sm:p-5 transition-colors hover:bg-gray-50 cursor-pointer"
                >
                  {content}
                </Link>
              ) : (
                <div
                  key={feature.key}
                  className="flex items-center justify-between p-4 sm:p-5 transition-colors cursor-default opacity-75"
                >
                  {content}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upgrade CTA */}
        <div className="mt-8 p-4 sm:p-6 bg-gradient-to-br from-gray-900 to-black rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-6 h-6" />
            <h2 className="text-lg font-semibold">Need more features?</h2>
          </div>
          <p className="text-sm text-gray-300 mb-4">
            Upgrade your plan to unlock Custom Branding, Priority Support, Advanced Booking, and Multi-Location.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VendorPlanFeatures;
